/**
 * Rollbar context helpers: tag every item with the app mode (mfe | standalone) and with
 * `inApp` = "confirmed to come from our own bundle".
 *
 * `inApp: false` means "not confirmed as ours" (third-party script, extension, cross-origin
 * "Script error." with no frames, ...). It does NOT claim to know where the error came from.
 */

export type RollbarMode = 'mfe' | 'standalone';

export interface RollbarFrame {
    filename?: string;
}

export interface RollbarContext {
    mode: RollbarMode;
    /** Base URL our bundle is served from (MFE bundle URL in MFE mode, app origin otherwise). */
    bundleBaseUrl: string;
    /** Only in MFE mode. */
    mfe?: {
        hostPageOrigin: string;
        organizationId?: string;
    };
}

function stripTrailingSlash (url: string): string {
    return url.endsWith('/') ? url.slice(0, -1) : url;
}

/**
 * Builds the `custom` payload set once at app start.
 */
export function buildRollbarContext (options: {
    isMicrofrontend: boolean;
    mfeBaseUrl?: string | null | undefined;
    hostPageOrigin: string;
    organizationId?: string | null | undefined;
}): RollbarContext {
    if (!options.isMicrofrontend) {
        return { mode: 'standalone', bundleBaseUrl: stripTrailingSlash(options.hostPageOrigin) };
    }

    return {
        mode: 'mfe',
        bundleBaseUrl: stripTrailingSlash(options.mfeBaseUrl || options.hostPageOrigin),
        mfe: {
            hostPageOrigin: options.hostPageOrigin,
            ...(options.organizationId ? { organizationId: options.organizationId } : {}),
        },
    };
}

/**
 * True only when the innermost frame (where the error was thrown) is served from our bundle.
 */
export function isFromApp (frames: ReadonlyArray<RollbarFrame> | undefined, bundleBaseUrl: string): boolean {
    if (!frames?.length || !bundleBaseUrl) {
        return false;
    }
    const filename = frames[frames.length - 1]?.filename;
    return typeof filename === 'string' && filename.startsWith(stripTrailingSlash(bundleBaseUrl));
}

interface RollbarPayloadData {
    custom?: { inApp?: boolean; bundleBaseUrl?: string };
    body?: {
        trace?: { frames?: RollbarFrame[] };
        trace_chain?: Array<{ frames?: RollbarFrame[] }>;
    };
}

/**
 * Rollbar `transform` hook: sets `custom.inApp`. Items raised by our own explicit
 * `rollbar.*` calls arrive with `inApp: true` already and are left alone.
 */
export function rollbarTransform (data: RollbarPayloadData): void {
    const custom = data.custom ?? (data.custom = {});
    if (custom.inApp === true) {
        return;
    }
    const frames = data.body?.trace?.frames ?? data.body?.trace_chain?.[0]?.frames;
    const bundleBaseUrl = custom.bundleBaseUrl ?? window.location.origin;
    custom.inApp = isFromApp(frames, bundleBaseUrl);
}
